import { useState, useCallback, useMemo, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore, useProgramStore } from '../../stores'
import { useToast } from '../common/Toast'
import { Button, LoadingScreen } from '../common'
import {
  type ProgramWizardStateV2,
  type ProgramBasicsData,
  type ProgramBasicsDataV2,
  type ProgramTaskData,
  type AssignmentStepData,
  DEFAULT_WIZARD_STATE_V2,
  getWeeksFromDurationId,
  getDurationIdFromWeeks,
} from '../../types/program-scheduling.types'
import { assignmentService, programService } from '../../services'
import { useDurationPresets } from '../../hooks'
import type { SubscriptionTier, AssignmentInsert } from '../../types/database.types'
import { ProgramBasicsStep } from './ProgramBasicsStep'
import { TaskSchedulerStep } from './TaskSchedulerStep'
import { AssignmentStep } from './AssignmentStep'

interface WizardStepConfig {
  id: string
  title: string
  shortTitle: string
  description: string
}

const WIZARD_STEPS: WizardStepConfig[] = [
  {
    id: 'basics',
    title: 'Program Basics',
    shortTitle: 'Basics',
    description: 'Name your program and set duration',
  },
  {
    id: 'exercises',
    title: 'Add Exercises',
    shortTitle: 'Exercises',
    description: 'Add exercises and upload videos',
  },
  {
    id: 'assign',
    title: 'Assign Clients',
    shortTitle: 'Assign',
    description: 'Optionally assign clients to this program',
  },
]

interface ProgramWizardProps {
  editingProgramId?: string
  initialState?: Partial<ProgramWizardStateV2>
  onComplete?: (programId: string) => void
}

export function ProgramWizard({
  editingProgramId,
  initialState,
}: ProgramWizardProps) {
  const navigate = useNavigate()
  const { profile } = useAuthStore()
  const { createProgram, updateProgram } = useProgramStore()
  const toast = useToast()
  const { customDurations } = useDurationPresets()

  const [state, setState] = useState<ProgramWizardStateV2>({
    ...DEFAULT_WIZARD_STATE_V2,
    ...initialState,
  })
  const [isSaving, setIsSaving] = useState(false)
  const [saveStep, setSaveStep] = useState<'program' | 'items' | 'assignments' | 'emails'>('program')
  const [isLoadingProgram, setIsLoadingProgram] = useState(!!editingProgramId)

  const currentStepConfig = WIZARD_STEPS[state.currentStep]
  const isFirstStep = state.currentStep === 0
  const isLastStep = state.currentStep === WIZARD_STEPS.length - 1

  const providerTier = profile?.subscription_tier as SubscriptionTier | undefined

  // Use ref to avoid infinite loop - customDurations changes reference on every render
  const customDurationsRef = useRef(customDurations)
  customDurationsRef.current = customDurations

  // Track if we've already loaded to prevent re-fetching
  const hasLoadedRef = useRef(false)

  // Track original item IDs for edit mode sync (to detect deletions)
  const [originalItemIds, setOriginalItemIds] = useState<Set<string>>(new Set())

  // Load existing program data in edit mode
  useEffect(() => {
    if (!editingProgramId || hasLoadedRef.current) return

    const loadProgram = async () => {
      try {
        hasLoadedRef.current = true
        setIsLoadingProgram(true)
        const program = await programService.getProgramById(editingProgramId)

        if (!program) {
          toast.error('Program not found')
          navigate('/dashboard/programs')
          return
        }

        // Transform program to wizard state format
        const basicsData: ProgramBasicsDataV2 = {
          title: program.title,
          description: program.description || '',
          programType: 'individual', // Default - field may not exist on older programs
          visibility: program.visibility,
          difficultyLevel: program.difficulty_level,
          isTemplate: program.is_template,
          duration: getDurationIdFromWeeks(program.duration_weeks, customDurationsRef.current),
        }

        // Transform program items to task format
        const tasksData: ProgramTaskData[] = program.items.map((item) => ({
          id: item.id,
          exerciseName: item.video?.title || 'Unknown Exercise',
          videoId: item.video_id,
          video: item.video ? {
            id: item.video.id,
            title: item.video.title,
            thumbnail_url: item.video.thumbnail_url,
            duration_seconds: item.video.duration_seconds,
            mux_playback_id: item.video.mux_playback_id,
            thumbnail_time: item.video.thumbnail_time,
          } : undefined,
          notes: item.notes || undefined,
          sets: item.sets || undefined,
          reps: item.reps || undefined,
          durationSeconds: item.duration_seconds || undefined,
          restSeconds: item.rest_seconds || undefined,
        }))

        // Store original item IDs for sync detection
        setOriginalItemIds(new Set(program.items.map((item) => item.id)))

        setState((prev) => ({
          ...prev,
          basics: basicsData,
          tasks: tasksData,
          createdProgramId: editingProgramId, // Mark as existing program
          isDirty: false,
        }))
      } catch (error) {
        console.error('Failed to load program:', error)
        toast.error('Failed to load program')
      } finally {
        setIsLoadingProgram(false)
      }
    }

    loadProgram()
  }, [editingProgramId, toast, navigate])

  // Validate current step
  const isCurrentStepValid = useMemo(() => {
    switch (state.currentStep) {
      case 0: // Basics
        return state.basics.title.trim().length > 0
      case 1: // Exercises
        return true // Exercises are optional
      case 2: // Assignment
        // Valid if skip mode OR if proper selection made
        if (state.assignment.mode === 'skip') return true
        if (state.assignment.mode === 'individual') return !!state.assignment.clientId
        if (state.assignment.mode === 'multiple') return state.assignment.selectedClientIds.size > 0
        if (state.assignment.mode === 'group') return !!state.assignment.groupId && state.assignment.selectedClientIds.size > 0
        return false
      default:
        return false
    }
  }, [state])

  // Update basics data
  const handleBasicsChange = useCallback((basics: ProgramBasicsData | ProgramBasicsDataV2) => {
    setState((prev) => ({
      ...prev,
      basics: basics as ProgramBasicsDataV2,
      isDirty: true,
    }))
  }, [])

  // Update tasks data
  const handleTasksChange = useCallback((tasks: ProgramTaskData[]) => {
    setState((prev) => ({
      ...prev,
      tasks,
      isDirty: true,
    }))
  }, [])

  // Update assignment data
  const handleAssignmentChange = useCallback((assignment: AssignmentStepData) => {
    setState((prev) => ({
      ...prev,
      assignment,
      isDirty: true,
    }))
  }, [])

  // Navigate between steps
  const goToStep = useCallback((step: number) => {
    if (step >= 0 && step < WIZARD_STEPS.length) {
      setState((prev) => ({
        ...prev,
        currentStep: step,
      }))
    }
  }, [])

  // Save the program (without assignments)
  const handleSaveProgram = useCallback(async () => {
    if (!profile?.id) return

    setIsSaving(true)
    setSaveStep('program')

    try {
      const durationWeeks = getWeeksFromDurationId(state.basics.duration, customDurations)

      const programData = {
        title: state.basics.title.trim(),
        description: state.basics.description.trim() || null,
        visibility: state.basics.visibility,
        difficulty_level: state.basics.difficultyLevel,
        is_template: state.basics.isTemplate,
        duration_weeks: durationWeeks,
      }

      let programId: string

      if (editingProgramId) {
        await updateProgram(editingProgramId, programData)
        programId = editingProgramId
      } else {
        const newProgram = await createProgram({
          ...programData,
          user_id: profile.id,
        })
        programId = newProgram.id
      }

      // Save program items (exercises)
      if (state.tasks.length > 0) {
        setSaveStep('items')
        for (const task of state.tasks) {
          await programService.addVideoToProgram(programId, task.videoId, {
            notes: task.notes || null,
            sets: task.sets || null,
            reps: task.reps || null,
            duration_seconds: task.durationSeconds || null,
            rest_seconds: task.restSeconds || null,
          })
        }
      }

      // Store the created program ID for assignments step
      setState((prev) => ({
        ...prev,
        createdProgramId: programId,
        isDirty: false,
      }))

      toast.success(editingProgramId ? 'Program updated' : 'Program created')

      // Move to assignment step
      goToStep(2)
    } catch (error) {
      console.error('Failed to save program:', error)
      toast.error('Failed to save program')
    } finally {
      setIsSaving(false)
    }
  }, [
    profile,
    state,
    editingProgramId,
    updateProgram,
    createProgram,
    customDurations,
    toast,
    goToStep,
  ])

  const handleNext = useCallback(async () => {
    if (!isCurrentStepValid) return

    // On step 2 (exercises) → save program before going to assignments
    if (state.currentStep === 1 && !state.createdProgramId && !editingProgramId) {
      await handleSaveProgram()
    } else if (!isLastStep) {
      goToStep(state.currentStep + 1)
    }
  }, [isCurrentStepValid, isLastStep, state.currentStep, state.createdProgramId, editingProgramId, handleSaveProgram, goToStep])

  const handleBack = useCallback(() => {
    if (!isFirstStep) {
      goToStep(state.currentStep - 1)
    }
  }, [isFirstStep, state.currentStep, goToStep])

  // Save with assignments (final step)
  const handleSaveWithAssignments = useCallback(async () => {
    const programId = state.createdProgramId || editingProgramId
    if (!profile?.id || !programId) return

    // If skipping assignments, navigate to program detail page
    if (state.assignment.mode === 'skip') {
      navigate(`/dashboard/programs/${programId}`)
      return
    }

    setIsSaving(true)

    try {
      // In edit mode with changes, sync program data first
      if (editingProgramId && state.isDirty) {
        setSaveStep('program')
        const durationWeeks = getWeeksFromDurationId(state.basics.duration, customDurations)

        const programData = {
          title: state.basics.title.trim(),
          description: state.basics.description.trim() || null,
          visibility: state.basics.visibility,
          difficulty_level: state.basics.difficultyLevel,
          is_template: state.basics.isTemplate,
          duration_weeks: durationWeeks,
        }

        await updateProgram(editingProgramId, programData)

        // Sync exercise changes
        setSaveStep('items')

        const currentIds = new Set(state.tasks.map((t) => t.id))
        const itemsToDelete = Array.from(originalItemIds).filter(
          (id) => !currentIds.has(id)
        )

        const itemsToAdd = state.tasks.filter(
          (task) => !originalItemIds.has(task.id!) || task.id!.startsWith('task-')
        )

        for (const itemId of itemsToDelete) {
          await programService.removeVideoFromProgram(itemId)
        }

        for (const task of itemsToAdd) {
          await programService.addVideoToProgram(editingProgramId, task.videoId, {
            notes: task.notes || null,
            sets: task.sets || null,
            reps: task.reps || null,
            duration_seconds: task.durationSeconds || null,
            rest_seconds: task.restSeconds || null,
          })
        }

        const existingItemIds = state.tasks
          .filter((t) => originalItemIds.has(t.id!) && !t.id!.startsWith('task-'))
          .map((t) => t.id!)

        if (existingItemIds.length > 0) {
          await programService.reorderProgramItems(editingProgramId, existingItemIds)
        }
      }

      // Now handle assignments
      setSaveStep('assignments')

      // Calculate expiration based on program duration (with tier-based fallback)
      const durationWeeks = getWeeksFromDurationId(state.basics.duration, customDurations)
      const expiresAt = assignmentService.getAccessExpirationForProgramDuration(durationWeeks, providerTier || 'basic')

      let clientIds: string[] = []

      if (state.assignment.mode === 'individual' && state.assignment.clientId) {
        clientIds = [state.assignment.clientId]
      } else if (state.assignment.mode === 'multiple' || state.assignment.mode === 'group') {
        clientIds = Array.from(state.assignment.selectedClientIds)
      }

      if (clientIds.length > 0) {
        const assignments: AssignmentInsert[] = clientIds.map((clientId) => ({
          client_id: clientId,
          program_id: programId,
          provider_id: profile.id,
          notes: state.assignment.notes.trim() || null,
          expires_at: expiresAt.toISOString(),
          status: 'active',
        }))

        // Create assignments and get full details (including client info)
        const result = await assignmentService.createBulkAssignmentsWithDetails(assignments)
        const createdAssignments = result.assignments

        toast.success(`Program assigned to ${clientIds.length} client${clientIds.length > 1 ? 's' : ''}`)

        // Auto-send email notifications
        if (createdAssignments.length > 0) {
          setSaveStep('emails')

          const emailResults = await assignmentService.sendBulkAssignmentEmails(
            createdAssignments,
            state.basics.title,
            profile?.full_name || 'Your Coach',
            state.tasks.length
          )

          const successCount = emailResults.filter((r) => r.success).length
          const failCount = emailResults.filter((r) => !r.success).length

          if (successCount > 0 && failCount === 0) {
            toast.success(`${successCount} email notification${successCount > 1 ? 's' : ''} sent`)
          } else if (successCount > 0 && failCount > 0) {
            toast.success(`${successCount} email${successCount > 1 ? 's' : ''} sent, ${failCount} failed`)
          } else if (failCount > 0) {
            toast.error(`Failed to send ${failCount} email notification${failCount > 1 ? 's' : ''}`)
          }
        }
      }

      // Navigate to program detail page after finishing
      navigate(`/dashboard/programs/${programId}`)
    } catch (error) {
      console.error('Failed to create assignments:', error)
      toast.error('Failed to assign program to clients')
    } finally {
      setIsSaving(false)
    }
  }, [
    profile,
    state,
    editingProgramId,
    providerTier,
    toast,
    navigate,
    customDurations,
    updateProgram,
    originalItemIds,
  ])

  const handleCancel = useCallback(() => {
    if (state.isDirty) {
      if (!confirm('You have unsaved changes. Are you sure you want to leave?')) {
        return
      }
    }
    navigate('/dashboard/programs')
  }, [state.isDirty, navigate])

  // Skip assignment and finish (save changes in edit mode first)
  const handleSkipAndFinish = useCallback(async () => {
    // In edit mode, save any changes to basics/exercises before finishing
    if (editingProgramId && state.isDirty) {
      setIsSaving(true)
      setSaveStep('program')

      try {
        const durationWeeks = getWeeksFromDurationId(state.basics.duration, customDurations)

        const programData = {
          title: state.basics.title.trim(),
          description: state.basics.description.trim() || null,
          visibility: state.basics.visibility,
          difficulty_level: state.basics.difficultyLevel,
          is_template: state.basics.isTemplate,
          duration_weeks: durationWeeks,
        }

        await updateProgram(editingProgramId, programData)

        // Sync exercise changes (add/remove/reorder)
        setSaveStep('items')

        // 1. Find items to delete (were in original but not in current)
        const currentIds = new Set(state.tasks.map((t) => t.id))
        const itemsToDelete = Array.from(originalItemIds).filter(
          (id) => !currentIds.has(id)
        )

        // 2. Find items to add (new IDs not in original - starts with 'task-')
        const itemsToAdd = state.tasks.filter(
          (task) => !originalItemIds.has(task.id!) || task.id!.startsWith('task-')
        )

        // 3. Delete removed items
        for (const itemId of itemsToDelete) {
          await programService.removeVideoFromProgram(itemId)
        }

        // 4. Add new items
        for (const task of itemsToAdd) {
          await programService.addVideoToProgram(editingProgramId, task.videoId, {
            notes: task.notes || null,
            sets: task.sets || null,
            reps: task.reps || null,
            duration_seconds: task.durationSeconds || null,
            rest_seconds: task.restSeconds || null,
          })
        }

        // 5. Reorder if needed (get all current item IDs that are not new)
        const existingItemIds = state.tasks
          .filter((t) => originalItemIds.has(t.id!) && !t.id!.startsWith('task-'))
          .map((t) => t.id!)

        if (existingItemIds.length > 0) {
          await programService.reorderProgramItems(editingProgramId, existingItemIds)
        }

        toast.success('Program updated')
      } catch (error) {
        console.error('Failed to save program:', error)
        toast.error('Failed to save program')
        setIsSaving(false)
        return
      } finally {
        setIsSaving(false)
      }
    }

    // Navigate to program detail page after finishing
    const programId = state.createdProgramId || editingProgramId
    navigate(`/dashboard/programs/${programId}`)
  }, [state, editingProgramId, customDurations, updateProgram, toast, navigate, originalItemIds])

  // Render step indicator
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {WIZARD_STEPS.map((step, index) => {
        const isCompleted = index < state.currentStep
        const isActive = index === state.currentStep
        // In edit mode, all steps are navigable since the program already exists
        const isNavigable = editingProgramId ? true : isCompleted

        return (
          <div key={step.id} className="flex items-center">
            {/* Step circle */}
            <button
              onClick={() => isNavigable && !isActive && goToStep(index)}
              disabled={!isNavigable && !isActive}
              className={`
                flex items-center justify-center w-10 h-10 rounded-full font-medium transition-all
                ${isCompleted
                  ? 'bg-blue-600 text-white cursor-pointer hover:bg-blue-700'
                  : isActive
                  ? 'bg-blue-600 text-white'
                  : editingProgramId
                  ? 'bg-gray-300 text-gray-600 cursor-pointer hover:bg-gray-400'
                  : 'bg-gray-200 text-gray-500'
                }
              `}
            >
              {isCompleted ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                index + 1
              )}
            </button>

            {/* Step label */}
            <div className={`ml-3 ${index < WIZARD_STEPS.length - 1 ? 'mr-8' : ''}`}>
              <p className={`text-sm font-medium ${isActive ? 'text-blue-600' : 'text-gray-500'}`}>
                {step.shortTitle}
              </p>
            </div>

            {/* Connector line */}
            {index < WIZARD_STEPS.length - 1 && (
              <div className={`w-12 h-0.5 ${isCompleted || editingProgramId ? 'bg-blue-600' : 'bg-gray-200'}`} />
            )}
          </div>
        )
      })}
    </div>
  )

  // Render current step content
  const renderStepContent = () => {
    switch (state.currentStep) {
      case 0:
        return (
          <ProgramBasicsStep
            data={state.basics}
            onChange={handleBasicsChange}
            showDuration={true}
          />
        )
      case 1:
        return (
          <TaskSchedulerStep
            tasks={state.tasks}
            onChange={handleTasksChange}
          />
        )
      case 2:
        return (
          <AssignmentStep
            data={state.assignment}
            onChange={handleAssignmentChange}
            programId={state.createdProgramId || editingProgramId}
            providerId={profile?.id}
            isEditMode={!!editingProgramId}
          />
        )
      default:
        return null
    }
  }

  // Render navigation buttons based on step
  const renderNavigationButtons = () => {
    if (isLastStep) {
      // Assignment step - show Skip & Finish and Save & Assign
      const isSkipMode = state.assignment.mode === 'skip'
      const isEditMode = !!editingProgramId

      return (
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={handleBack}>
            Back
          </Button>

          <div className="flex items-center gap-3">
            {/* In edit mode with skip, just show Finish button. In create mode, show Skip & Finish */}
            <Button
              variant={isEditMode && isSkipMode ? 'primary' : 'outline'}
              onClick={handleSkipAndFinish}
            >
              {isEditMode ? (isSkipMode ? 'Finish' : 'Skip & Finish') : 'Skip & Finish'}
            </Button>
            {/* Only show the assign button when not in skip mode */}
            {!isSkipMode && (
              <Button
                onClick={handleSaveWithAssignments}
                disabled={!isCurrentStepValid}
              >
                {state.assignment.mode === 'individual'
                  ? 'Assign & Finish'
                  : `Assign to ${state.assignment.selectedClientIds.size} Client${state.assignment.selectedClientIds.size !== 1 ? 's' : ''}`}
              </Button>
            )}
          </div>
        </div>
      )
    }

    // Steps 1 and 2
    return (
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={isFirstStep ? handleCancel : handleBack}
        >
          {isFirstStep ? 'Cancel' : 'Back'}
        </Button>

        <div className="flex items-center gap-3">
          {state.currentStep === 1 && !state.createdProgramId && (
            <Button
              variant="outline"
              onClick={handleSaveProgram}
              disabled={!state.basics.title.trim()}
            >
              Save & Exit
            </Button>
          )}
          <Button
            onClick={handleNext}
            disabled={!isCurrentStepValid}
          >
            {state.currentStep === 1 && !state.createdProgramId
              ? 'Save & Continue'
              : 'Next'}
          </Button>
        </div>
      </div>
    )
  }

  if (isLoadingProgram) {
    return <LoadingScreen message="Loading program..." />
  }

  if (isSaving) {
    const messages = {
      program: 'Saving program...',
      items: 'Adding exercises...',
      assignments: 'Creating assignments...',
      emails: 'Sending email notifications...',
    }
    return <LoadingScreen message={messages[saveStep]} />
  }

  return (
    <div className="max-w-4xl mx-auto py-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">
          {editingProgramId ? 'Edit Program' : 'Create New Program'}
        </h1>
        <p className="mt-1 text-gray-600">{currentStepConfig.description}</p>
      </div>

      {/* Step indicator */}
      {renderStepIndicator()}

      {/* Step content */}
      <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">
          {currentStepConfig.title}
        </h2>
        {renderStepContent()}
      </div>

      {/* Navigation buttons */}
      {renderNavigationButtons()}
    </div>
  )
}
